import { Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {ConfirmationService, MessageService, TreeNode} from 'primeng/api';
import {NgForm} from '@angular/forms';
import {Company} from '../../types/company';
import {identityTypes} from '../../types/identity-type';
import {Shareholder} from '../../types/shareholder';
import {Attachment} from "../../types/attachment";
import {HttpEventType} from "@angular/common/http";
import {share, Subscription} from "rxjs";
import {FormService} from "../../services/form.service";
import {AttachmentType} from "../../types/attachment-type";
import {FileUpload} from "primeng/fileupload";
import {AppComponent} from "../../app.component";
import {InputMask} from "primeng/inputmask";
import {Constants} from "../../types/constants";

type AttachmentInternal = {
  attachment ?: Attachment,
  subscription ?: Subscription
}

@Component({
  selector: 'partners',
  templateUrl: './partners.component.html',
  styleUrls: ['./partners.component.css'],
  providers: [ConfirmationService]
})
export class PartnersComponent implements OnInit {
  @Input() docId !: number;
  @Input() company !: Company;
  @Input() shareholders !: Shareholder[];
  @Output() shareholdersChange = new EventEmitter<Shareholder[]>();
  @Output() onlyPeselsChange = new EventEmitter<boolean>();

  @ViewChild('addPartnerForm') addPartnerForm!: NgForm;
  @ViewChild('krkForm') krkFileUpload?: FileUpload;

  partners : TreeNode<Shareholder>[] = [];
  selectedPartner ?: TreeNode<Shareholder> = undefined;
  filledShareholders : Shareholder[] = [];
  displayShareholderAdding: boolean = false;
  partnersFormDirty: boolean = false;
  partnersNamesInvalid: boolean = false;
  partnersEmpty: boolean = false;
  noPesel : boolean = false;
  alreadyFilled: boolean = false; // currently editing shareholder was already properly filled
  copied: boolean = false;
  shareholder : Shareholder = { type: "OF_P_1", boardMember: true, personalData: {}};
  identityTypes = identityTypes;
  identityTypesMap : Map<string, string> = new Map([["PASZPORT","Paszport"], ["DOWOD","Dowód"], ["INNY_DOK","Inny dokument"]]);
  krkFile ?: AttachmentInternal;
  krkError : boolean = false;
  krkMap : Map<number, Attachment> = new Map();
  firstNameMask : string = "";
  firstNamePlaceholder : string = "";
  surnameMask : string = "";
  surnamePlaceholder : string = "";
  peselMask : string = "";
  peselPlaceholder : string = "";
  peselsOnly : boolean = true; // all partners have pesels
  identityFilter : RegExp = /^[\w\-\s]+$/;

  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private formService: FormService) { }

  ngOnInit(): void {
    this.shareholder = { type: "OF_P_1", boardMember: true, personalData: {}};
    this.displayShareholderAdding = false;
    this.partnersFormDirty = false;
    this.partnersEmpty = this.shareholders.length == 0;
    this.noPesel = false;
    this.alreadyFilled = false;
    this.krkFile = undefined;
    this.krkMap.clear();
    this.partners = this.shareholdersToTreeNodes(this.shareholders);
    this.filledShareholders = [];
    this.peselsOnly = true;
    this.updateIndexesAndPesels();
    this.recalculateNamesValidity();
    this.onlyPeselsChange.emit(this.peselsOnly);
  }

  shareholdersToTreeNodes(shareholders: Shareholder[]): TreeNode<Shareholder>[] {
    return shareholders.map(shr => {
      return {data: shr, expanded: true, children: shr.shareholders ? this.shareholdersToTreeNodes(shr.shareholders) : undefined};
    });
  }

  updateIndexesAndPesels() {
    this.updateIndexesRecursively(this.partners, "");
  }

  updateIndexesRecursively(nodes: TreeNode<Shareholder>[], label: string) {
    let i: number = 1;
    for (let node of nodes) {
      if (node.data?.type?.startsWith('OF_P') && !node.data?.personalData?.pesel) {
        this.peselsOnly = false;
      }
      let lab: string = label;
      if (lab.length > 0)
        lab += ".";
      lab += i;
      node.label = lab;
      if (node.children)
        this.updateIndexesRecursively(node.children, lab);
      ++i;
    }
  }

  noNipChanged(checked: boolean) : void {
    if (checked) {
      this.shareholder.nip = undefined;
    }
  }

  saveShareholder() {
    if (!this.copied)
      this.addPartnerForm.form.markAllAsTouched();
    let krkId = this.shareholder.personalData?.krkId;
    if (krkId == undefined || krkId == 0)
      this.krkError = true;
    if ((!this.copied && this.addPartnerForm.invalid) || krkId == undefined || krkId == 0) {
      this.messageService.add({
        severity:'warn',
        detail: 'Formularz wypełniony nieprawidłowo. Popraw błędy zaznaczone na czerwono' }
      );
      return;
    }

    if (this.shareholder.nip == this.company.nip) {
      this.messageService.add({
        severity:'warn',
        detail:'NIP członka zarządu/wspólnika nie może być taki sam jak NIP spółki.'  }
      );
      return;
    }
    if (!this.copied
      && this.shareholder.personalData
      && this.shareholder.personalData.pesel
      && this.warningIfDuplicate(this.shareholder.nip, this.shareholder.personalData.pesel)) {
      return;
    }

    if (this.shareholder.nip != this.selectedPartner!.data!.nip) {
      // copy nip to same entries
      this.copyNipToSameEntries(krkId, this.shareholder.nip, this.shareholder.noNip);
    }

    let shrs: Shareholder[] = this.selectedPartner!.parent ? this.selectedPartner!.parent.data!.shareholders! : this.shareholders;
    let index = shrs.indexOf(this.selectedPartner!.data!);
    shrs[index] = this.shareholder;
    this.selectedPartner!.data = this.shareholder;
    this.displayShareholderAdding = false;
    this.shareholdersChange.emit(this.shareholders);
    if (!this.alreadyFilled)
      this.filledShareholders.push(this.shareholder);
    this.recalculateNamesValidity();
  }

  private copyNipToSameEntries(krkId : number, nip: string | undefined, noNip: boolean | undefined) {
    for (let shareholder of this.shareholders)
      this.copyNipToSameEntriesRecur(shareholder, krkId, nip, noNip);
  }

  private copyNipToSameEntriesRecur(shareholder: Shareholder, krkId: number, nip: string | undefined, noNip: boolean | undefined) {
    if (shareholder.personalData?.krkId == krkId) {
      console.log("zmiana " + shareholder.nip + shareholder.noNip + " na " + nip, noNip);
      shareholder.nip = nip;
      shareholder.noNip = noNip;
    }
    if (shareholder.shareholders && shareholder.shareholders.length > 0) {
      for (let sh of shareholder.shareholders)
        this.copyNipToSameEntriesRecur(sh, krkId, nip, noNip);
    }
}

  private findSimilarShareholder(sh : Shareholder) : Shareholder | undefined {
    // podobny shareholder to taki który ma takie same inicjały i pierwszą cyfrę numeru PESEL
    let firstNameRegex = sh.personalData?.firstname?.replace(/(?<=\S)\S/g, "\\S") ?? "";
    let surnameRegex = sh.personalData?.surname?.replace(/(?<=\S)\S/g, "\\S") ?? "";
    let peselRegex = sh.personalData?.pesel?.replace(/(?<=\S)\S/g, "\\d") ?? "";
    return this.filledShareholders.find(fsh => {
      if (fsh.personalData?.firstname?.match(firstNameRegex)
      && fsh.personalData?.surname?.match(surnameRegex)
      && ((fsh.personalData?.pesel && peselRegex && fsh.personalData.pesel.match(peselRegex))
        || (!fsh.personalData.pesel && !peselRegex)))
        return true;
      return false;
    });
  }

  onShareholderRowEdit(shareholder: Shareholder, node: TreeNode<Shareholder>) {
    let personalData = {...shareholder.personalData};
    this.shareholder = {...shareholder};
    this.shareholder.personalData = {...personalData};
    this.selectedPartner = node;
    this.copied = false;

    if (personalData.krkId != null) {
      this.krkFile = {attachment: this.krkMap.get(personalData.krkId)};
      this.alreadyFilled = true;
      this.rowEdit();
    } else {
      this.alreadyFilled = false;
      let matchedShareholder: Shareholder | undefined = this.findSimilarShareholder(this.shareholder);
      if (matchedShareholder) {
        this.confirmationService.confirm({
          message: "Czy uzupełnić danymi:\n"
            + "Imię: " + matchedShareholder.personalData?.firstname + "\n"
            + "Nazwisko: " + matchedShareholder.personalData?.surname + "\n"
            + "NIP: " + (matchedShareholder.nip ? matchedShareholder.nip : "brak") + "\n"
            + "PESEL: " + matchedShareholder.personalData?.pesel,
          accept: () => this.rowCopy(matchedShareholder!),
          reject: () => this.clearAndEdit()
        });
      } else
        this.clearAndEdit();
    }
  }

  clearAndEdit() {
    this.shareholder.noNip = false; // user must click checkbox if there is no nip
    if (this.krkFileUpload) {
      this.krkFileUpload.clear();
      this.krkFileUpload.uploadedFileCount = 0;
      this.krkFileUpload.disabled = false;
    }
    this.rowEdit();
  }

  rowEdit() {
    this.firstNameMask = this.shareholder.personalData?.firstname?.replace(/(?<=\S)\S/g, 'a') ?? "";
    this.surnameMask = this.shareholder.personalData?.surname?.replace(/(?<=\S)\S/g, 'a') ?? "";
    this.peselMask = this.shareholder.personalData?.pesel?.replace(/(?<=\S)\S/g, '9') ?? "";
    this.firstNamePlaceholder = this.shareholder.personalData?.firstname!;
    this.surnamePlaceholder = this.shareholder.personalData?.surname!;
    this.peselPlaceholder = this.shareholder.personalData?.pesel!;

    if (this.shareholder.personalData?.firstname?.includes('*'))
      this.shareholder.personalData.firstname = undefined;
    if (this.shareholder.personalData?.surname?.includes('*'))
      this.shareholder.personalData.surname = undefined;
    if (this.shareholder.personalData?.pesel?.includes('*')) {
      this.shareholder.personalData.pesel = undefined;
      this.noPesel = false;
    } else
      this.noPesel = !this.shareholder.personalData?.pesel;

    this.displayShareholderAdding = true;
  }

  rowCopy(matchedShareholder: Shareholder) {
    this.alreadyFilled = true;
    this.copied = true;
    this.shareholder = {...matchedShareholder};
    this.shareholder.personalData = {...matchedShareholder.personalData};
    this.saveShareholder();
  }

  uploadHandler(event: any, fileUpload: FileUpload) {
    let file = event.files[0];
    if (!file)
      return;
    if (this.krkFile && this.krkFile.attachment)
      this.krkMap.delete(this.krkFile.attachment.id);

    if (!this.shareholder.personalData?.firstname) {
      this.messageService.add({
        severity:'warn',
        detail: "Najpierw podaj imię" }
      );
      fileUpload.clear();
      return;
    }

    if (!this.shareholder.personalData?.surname) {
      this.messageService.add({
        severity:'warn',
        detail: "Najpierw podaj nazwisko" }
      );
      fileUpload.clear();
      return;
    }

    let userId = this.shareholder.personalData?.pesel ? this.shareholder.personalData.pesel : this.shareholder.personalData?.identityValue;
    if (!userId) {
      this.messageService.add({
        severity:'warn',
        detail: "Najpierw podaj " + (this.noPesel ? "rodzaj i numer dokumentu" : "numer pesel")}
      );
      fileUpload.clear();
      return;
    }

    let attachment: Attachment = {name: file.name, id: 0};
    this.krkFile = {attachment: attachment};
    fileUpload.disabled = true;
    fileUpload.progress = 0;

    this.krkFile.subscription = this.formService.uploadFile(file, this.docId, AttachmentType.KRK, this.shareholder.personalData.firstname,
      this.shareholder.personalData.surname, this.shareholder.personalData.pesel)
      .subscribe(
        event => {
          if (event.type === HttpEventType.UploadProgress) {
            fileUpload.progress = Math.round(100 * (event.loaded / event.total));
            fileUpload.cd.markForCheck();
          }
          if (event.type === HttpEventType.Response) {
            let krkId =  Number(event.body.message);
            attachment.id = krkId;
            this.shareholder.personalData!.krkId = krkId;
            this.krkMap.set(krkId, attachment);
            this.krkError = false;
          }
        },
        error => {
          this.messageService.add({
            severity: (error.status == 400 && error.error.message != Constants.GenericError) ? 'warn' : 'error',
            detail: error.error.message ?? Constants.GenericError}
          );
          this.krkFile = undefined;
          this.krkError = true;
          fileUpload.clear();
          fileUpload.uploadedFileCount = 0;
          fileUpload.disabled = false;
        }
      );
  }
  fileRemove(fileUpload: FileUpload) {
    if (this.krkFile!.subscription != undefined) {
      this.krkFile!.subscription.unsubscribe();
      this.krkFile!.subscription = undefined;
    }
    this.krkFile = undefined;
    this.krkError = true;
    this.shareholder.personalData!.krkId = undefined;
    fileUpload.uploadedFileCount = 0;
    fileUpload.progress = 0;
    fileUpload.disabled = false;
  }

  onDialogClosed() {
    if (this.krkFile && this.krkFile.subscription != undefined) {
      this.krkFile.subscription.unsubscribe();
      this.krkFile.subscription = undefined;
    }
    this.krkFile = undefined;
  }

  setFormDirty(dirty : boolean) {
    this.partnersFormDirty = dirty;
  }

  editFormInvalid() : boolean {
    if (this.partnersEmpty || (this.partnersFormDirty && this.formInvalid())) {
      return true;
    }
    return false;
  }

  formInvalid() : boolean {
    return this.partnersEmpty || this.partnersNamesInvalid;
  }

  private warningIfDuplicate(nip: string | undefined, pesel: string) : boolean {
    for (let shareholder of this.shareholders) {
      if (this.warnIfDup(shareholder, nip, pesel))
        return true;
    }
    return false;
  }

  private warnIfDup(shareholder: Shareholder, nip: string | undefined, pesel: string) : boolean {
    if (nip && shareholder.nip == nip && nip != this.selectedPartner?.data?.nip) {
      if (shareholder.personalData) {
        this.messageService.add({
            severity: 'warn',
            detail: 'NIP występuje już u innego członka zarządu/wspólnika.'
          }
        );
      } else {
        this.messageService.add({
            severity: 'warn',
            detail: 'NIP członka zarządu/wspólnika nie może być taki sam jak NIP spółki.'
          }
        );
      }
      return true;
    }
    if (shareholder.personalData && pesel == shareholder.personalData.pesel && pesel != this.selectedPartner?.data?.personalData?.pesel) {
      this.messageService.add({
          severity: 'warn',
          detail: 'PESEL występuje już u innego członka zarządu.'
        }
      );
      return true;
    }
    if (shareholder.shareholders && shareholder.shareholders.length > 0) {
      for (let sh of shareholder.shareholders) {
        if (this.warnIfDup(sh, nip, pesel))
          return true;
      }
    }
    return false;
  }

  // przeliczenie poprawności imion i nazwisk, czy nie zawierają gwiazdek
  private recalculateNamesValidity() {
    for (let shareholder of this.shareholders) {
      if (this.isPartnerInvalid(shareholder)) {
        this.partnersNamesInvalid = true;
        return;
      }
    }
    this.partnersNamesInvalid = false;
  }

  isPartnerInvalid(shareholder: Shareholder) : boolean {
    if (shareholder.personalData?.firstname?.includes('*')
      || shareholder.personalData?.surname?.includes('*')
      || shareholder.personalData?.pesel?.includes('*')) {
      return true;
    }
    if (shareholder.shareholders && shareholder.shareholders.length > 0) {
      for (let sh of shareholder.shareholders) {
        if (this.isPartnerInvalid(sh))
          return true;
      }
    }
    return false;
  }

  shareholdersToPartners(shareholder: Shareholder, label: string) {
    if (shareholder.shareholders && shareholder.shareholders.length > 0) {
      let i:number = 1;
      for (let sh of shareholder.shareholders) {
        this.shareholdersToPartners(sh, label + "." + i);
        ++i;
      }
    }
  }

}
